import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Heading, Text, Button, Layer, Table, TableHeader, TableRow, TableCell,
    TableBody, Spinner, DropButton
} from "grommet";
import { FormDown } from "grommet-icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";
import { ProductsAssessment } from "../../components/PursuitAccess/ProductsAssessment";
import { DigitalLearningPath } from "../../components/QuizResultsComponents/DigitalLearningPath";
import { HeaderNavigation } from "../../components/Headers/PursuitHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { LearningPathModal } from "../../components/PursuitAccess/LearningPathModal";
import ExportUserAssessmentsButton from "../../components/PursuitAccess/ExportUserAssessmentsButton";

/* --- Single detailed block (compact for current) --- */
const AssessmentBlock = ({ user_id, assessment_id, compact = false }) => {
    const [showLP, setShowLP] = useState(false);
    const [skillId, setSkillId] = useState(null);

    return (
        <Box
            as="section"
            background="white"
            pad={compact ? "small" : "medium"}
            round="small"
            elevation="small"
            fill="horizontal"
            style={{ overflow: "visible", position: "relative", minWidth: 0 }}
        >
            <ProductsAssessment
                user_id={user_id}
                assessment_id={assessment_id}
                onShowLearningPath={(id) => { setSkillId(id); setShowLP(true); }}
            />

            {showLP && (
                <Box margin={{ top: "small" }} background="light-2" pad="small" round="small" style={{ overflow: "visible" }}>
                    <LearningPathModal
                        user_id={user_id}
                        skills_id={skillId}
                        assessment_id={assessment_id}
                        onClose={() => setShowLP(false)}
                    />
                </Box>
            )}

            <DigitalLearningPath user_id={user_id} assessment_id={assessment_id} />
        </Box>
    );
};

const RecommendationsPage = () => {
    const OTHER_RANGES = [
        { label: "All", value: "all", days: null },
        { label: "1 day", value: "1d", days: 1 },
        { label: "1 week", value: "1w", days: 7 },
        { label: "1 month", value: "1m", days: 30 },
        { label: "3 months", value: "3m", days: 90 },
        { label: "6 months", value: "6m", days: 180 },
    ];

    const { user_id, assessment_id } = useParams();

    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [errorSummary, setErrorSummary] = useState();

    const [allIds, setAllIds] = useState([]);
    const [loadingIds, setLoadingIds] = useState(true);
    const [errorIds, setErrorIds] = useState();

    // summaries for "other assessments" table
    const [summariesById, setSummariesById] = useState({});
    const [loadingOtherSummaries, setLoadingOtherSummaries] = useState(false);

    // Modals
    const [showOthersModal, setShowOthersModal] = useState(false);
    const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
    const [showOverview, setShowOverview] = useState(false);
    const [showQuestions, setShowQuestions] = useState(false);
    const [otherRange, setOtherRange] = useState("all");

    const labelFor = (v) => OTHER_RANGES.find(r => r.value === v)?.label ?? "All";
    const inRange = (endISO, value) => {
        if (value === "all") return true;
        const spec = OTHER_RANGES.find(r => r.value === value);
        if (!spec?.days || !endISO) return false;
        const end = new Date(endISO);
        const since = new Date(Date.now() - spec.days * 24 * 60 * 60 * 1000);
        return end >= since;
    };

    const fmtDate = (iso) =>
        iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";



    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/manager/assessmentSummary/${assessment_id}`);
                setSummary(data.assessmentSummary?.[0] || null);
            } catch {
                setErrorSummary("Unable to load assessment details.");
            } finally {
                setLoadingSummary(false);
            }
        })();
    }, [assessment_id]);

    useEffect(() => {
        (async () => {
            setLoadingIds(true);
            try {
                const { data } = await axios.get(`${API_URL}/pursuit/${user_id}/completed-assessment-ids`);
                setAllIds(Array.isArray(data.assesment_ids) ? data.assesment_ids : []);
            } catch {
                setErrorIds("Unable to load user's completed assessments.");
            } finally {
                setLoadingIds(false);
            }
        })();
    }, [user_id]);

    const otherIds = useMemo(
        () => allIds.filter((id) => String(id) !== String(assessment_id)),
        [allIds, assessment_id]
    );

    // Preload summaries for table rows
    useEffect(() => {
        const idsToFetch = otherIds.filter((id) => !summariesById[String(id)]);
        if (idsToFetch.length === 0) return;

        setLoadingOtherSummaries(true);
        Promise.allSettled(idsToFetch.map((id) => axios.get(`${API_URL}/manager/assessmentSummary/${id}`)))
            .then((results) => {
                const next = { ...summariesById };
                results.forEach((res, idx) => {
                    const id = String(idsToFetch[idx]);
                    next[id] = res.status === "fulfilled" ? (res.value.data.assessmentSummary?.[0] || null) : null;
                });
                setSummariesById(next);
            })
            .finally(() => setLoadingOtherSummaries(false));
    }, [otherIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredOtherIds = useMemo(() => {
        if (otherRange === "all") return otherIds;
        return otherIds.filter((id) => {
            const s = summariesById[String(id)];
            return inRange(s?.end_date, otherRange);
        });
    }, [otherIds, summariesById, otherRange]);

    return (
        <Box height="100vh" direction="column" overflow="hidden">
            <HeaderNavigation />

            <Box
                as="main"
                flex={{ grow: 1, shrink: 1 }}
                overflow={{ vertical: "auto" }}
                pad={{ horizontal: "medium", top: "medium", bottom: "xlarge" }}
                style={{ minHeight: 0, minWidth: 0 }}
            >
                {/* Header actions */}
                <Box direction="row" align="center" justify="between" margin={{ bottom: "small" }}>
                    <Heading level={2} margin="none">Recommendations for Assessment</Heading>
                    <Box direction="row" gap="small">
                        <ExportUserAssessmentsButton
                            apiBase={API_URL}
                            assessmentIds={allIds}
                            userId={user_id}
                            defaultRange="6m"
                        />
                        <Button
                            label="View Assessment Overview"
                            onClick={() => setShowOverview(true)}
                            secondary
                            size="medium"
                            disabled={loadingSummary || !!errorSummary}
                        />
                        <Button
                            label="View Assessment Questions"
                            onClick={() => setShowQuestions(true)}
                            secondary
                            size="medium"
                            disabled={loadingSummary || !!errorSummary || !summary?.questions?.length}
                        />
                    </Box>
                </Box>

                {/* Current assessment (compact) */}
                <Heading level={3} margin={{ bottom: "medium", top: "none" }}>
                    Current Assessment
                </Heading>
                <AssessmentBlock
                    user_id={user_id}
                    assessment_id={String(assessment_id)}
                    compact
                />

                <Box
                    direction="row"
                    align="center"
                    gap="small"
                    margin={{ top: "large", bottom: "none" }}
                >
                    <Heading level={3} color="text-strong" margin="none">
                        View other assessments for this user
                    </Heading>

                    <Button
                        label="Show Other Assessments"
                        onClick={() => setShowOthersModal(true)}
                        secondary
                        disabled={loadingIds || !!errorIds || otherIds.length === 0}
                    />
                </Box>

            </Box>

            <StickyFooter />

            {/* Modal: all OTHER assessments as a SCROLLABLE TABLE */}
            {showOthersModal && (
                <Layer
                    onEsc={() => setShowOthersModal(false)}
                    onClickOutside={() => setShowOthersModal(false)}
                    responsive={false}
                >
                    <Box pad="medium" width="xlarge" gap="small">
                        {/* Top row: timeframe dropdown (left) + title */}
                        <Box direction="row" align="center" justify="between">

                            <Heading level={2} margin="none">Other Assessments</Heading>
                            <Box direction="row" align="center" gap="small" wrap={false}>
                                <Text weight="bold">Timeframe:</Text>

                                <DropButton
                                    dropAlign={{ top: "bottom", left: "left" }}
                                    label={
                                        <Box direction="row" align="center" gap="xsmall">
                                            <Text>{labelFor(otherRange)}</Text>
                                            <FormDown />
                                        </Box>
                                    }
                                    dropContent={
                                        <Box pad="small" gap="xsmall" width="small">
                                            {OTHER_RANGES.map((opt) => (
                                                <Button
                                                    key={opt.value}
                                                    plain
                                                    hoverIndicator
                                                    onClick={() => setOtherRange(opt.value)}
                                                >
                                                    <Box pad="xsmall">
                                                        <Text color={otherRange === opt.value ? "brand" : undefined}>
                                                            {opt.label}
                                                        </Text>
                                                    </Box>
                                                </Button>
                                            ))}
                                        </Box>
                                    }
                                />
                            </Box>

                        </Box>

                        {loadingIds ? (
                            <Box direction="row" gap="small" align="center">
                                <Spinner /><Text>Loading assessments…</Text>
                            </Box>
                        ) : errorIds ? (
                            <Text color="status-critical">{errorIds}</Text>
                        ) : filteredOtherIds.length === 0 ? (
                            <Text>
                                {otherIds.length === 0
                                    ? "No other completed assessments for this user."
                                    : "No assessments in the selected timeframe."}
                            </Text>
                        ) : (
                            // scrollable table
                            <Box height="60vh" overflow={{ vertical: "auto" }}>
                                {loadingOtherSummaries && (
                                    <Box direction="row" gap="xsmall" align="center" pad={{ top: "small" }}>
                                        <Spinner />
                                        <Text size="medium">Fetching details…</Text>
                                    </Box>
                                )}
                                {!loadingOtherSummaries && (
                                    <Table fill style={{ tableLayout: "fixed", width: "100%" }}>
                                        <TableHeader>
                                            <TableRow>
                                                <TableCell scope="col" align="start" style={{ width: 64 }}>
                                                    <Text weight="bold">#</Text>
                                                </TableCell>
                                                <TableCell scope="col">
                                                    <Text weight="bold">Date</Text>
                                                </TableCell>
                                                <TableCell scope="col">
                                                    <Text weight="bold">Questions</Text>
                                                </TableCell>
                                                <TableCell scope="col">
                                                    <Text weight="bold">Correct</Text>
                                                </TableCell>
                                                <TableCell scope="col">
                                                    <Text weight="bold">Incorrect</Text>
                                                </TableCell>
                                                <TableCell scope="col" align="start" style={{ width: 240 }}>
                                                    <Text weight="bold">Action</Text>
                                                </TableCell>
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            {filteredOtherIds.map((id, idx) => {
                                                const s = summariesById[String(id)];
                                                return (
                                                    <TableRow key={String(id)}>
                                                        <TableCell align="start">
                                                            <Text size="medium">{idx + 1}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Text size="medium">{s ? fmtDate(s.end_date) : "—"}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Text size="medium">{s?.query_count ?? "—"}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Text size="medium">{s?.correct_count ?? "—"}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Text size="medium">{s?.incorrect_count ?? "—"}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box align="end">
                                                                <Button
                                                                    label="View assessment details"
                                                                    secondary
                                                                    onClick={() => setSelectedAssessmentId(String(id))}
                                                                    style={{ width: "auto", minWidth: "unset", whiteSpace: "nowrap" }}
                                                                />
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        )}

                        {/* Bottom-right Close button */}
                        <Box direction="row" justify="end" margin={{ top: "small" }}>
                            <Button secondary label="Close" onClick={() => setShowOthersModal(false)} />
                        </Box>
                    </Box>
                </Layer>
            )}

            {/* Modal: full detail for a selected OTHER assessment */}
            {selectedAssessmentId && (
                <Layer
                    onEsc={() => setSelectedAssessmentId(null)}
                    onClickOutside={() => setSelectedAssessmentId(null)}
                    responsive={false}
                >
                    <Box pad="medium" width="xlarge" height={{ max: "85vh" }} overflow={{ vertical: "auto" }}>
                        <Box direction="row" align="center" justify="between" margin={{ bottom: "small" }}>
                            <Heading level={3} margin="none">Assessment Details</Heading>
                            <Button label="Close" onClick={() => setSelectedAssessmentId(null)} />
                        </Box>
                        <AssessmentBlock user_id={user_id} assessment_id={selectedAssessmentId} />
                    </Box>
                </Layer>
            )}

            {/* Current assessment: Overview */}
            {showOverview && (
                <Layer onEsc={() => setShowOverview(false)} onClickOutside={() => setShowOverview(false)} responsive={false}>
                    <Box pad="medium" width="large" height={{ max: "70vh" }} overflow={{ vertical: "auto" }} gap="medium">
                        <Heading level={3} margin="none">Assessment Overview</Heading>
                        {loadingSummary ? (
                            <Spinner margin={{ vertical: "small" }} />
                        ) : errorSummary ? (
                            <Text color="status-critical">{errorSummary}</Text>
                        ) : summary ? (
                            <Box gap="small">
                                <Box direction="row" gap="xsmall" align="center">
                                    <Text weight="bold">User:</Text><Text>{summary.name}</Text>
                                </Box>
                                <Box direction="row" gap="xsmall" align="center">
                                    <Text weight="bold">Date:</Text>
                                    <Text>
                                        {fmtDate(summary.start_date)} {summary.start_time}{"  →  "}
                                        {fmtDate(summary.end_date)} {summary.end_time}
                                    </Text>
                                </Box>
                                <Box direction="row" gap="small" align="center">
                                    <Box direction="row" gap="xsmall" align="center">
                                        <Text weight="bold">Questions:</Text><Text>{summary.query_count}</Text>
                                    </Box>
                                    <Box direction="row" gap="xsmall" align="center">
                                        <Text weight="bold">Correct:</Text><Text>{summary.correct_count}</Text>
                                    </Box>
                                    <Box direction="row" gap="xsmall" align="center">
                                        <Text weight="bold">Incorrect:</Text><Text>{summary.incorrect_count}</Text>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Text>No overview available.</Text>
                        )}
                        <Box align="end" margin={{ top: "medium" }}>
                            <Button label="Close" onClick={() => setShowOverview(false)} size="small" />
                        </Box>
                    </Box>
                </Layer>
            )}

            {/* Current assessment: Questions */}
            {showQuestions && (
                <Layer onEsc={() => setShowQuestions(false)} onClickOutside={() => setShowQuestions(false)} responsive={false}>
                    <Box pad="medium" width="large" margin="medium" align="center" overflow="auto" style={{ maxHeight: "80vh" }}>
                        <Heading level={3} margin={{ bottom: "small" }}>Questions for this assessment</Heading>
                        {loadingSummary ? (
                            <Spinner />
                        ) : !summary?.questions?.length ? (
                            <Text>No questions available.</Text>
                        ) : (
                            <Table align="center">
                                <TableHeader>
                                    <TableRow>
                                        <TableCell scope="col" border="bottom"><Text weight="bold">Question</Text></TableCell>
                                        <TableCell scope="col" border="bottom"><Text weight="bold">Skill</Text></TableCell>
                                        <TableCell scope="col" border="bottom"><Text weight="bold">Answer</Text></TableCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {summary.questions.map((q) => (
                                        <TableRow key={q.id}>
                                            <TableCell><Text>{q.question}</Text></TableCell>
                                            <TableCell><Text>{q.skill}</Text></TableCell>
                                            <TableCell><Text weight="bold">{q.correct}</Text></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <Box align="center" pad="medium">
                            <Button label="Close" onClick={() => setShowQuestions(false)} />
                        </Box>
                    </Box>
                </Layer>
            )}
        </Box>
    );
};

export default RecommendationsPage;

